/** Evaluate one merchant's charges for a recurring pattern and per-sub flags. */

import type { Flag, Subscription, Transaction } from '../types';
import { category } from '../merchants/normalize';
import { CADENCE_LABEL, DAY, matchCadence, median } from './cadence';
import { clusterByAmount } from './cluster';
import type { MoneyFormatter } from './format';
import { fmtDate } from './format';

export interface MerchantGroup {
  key: string;
  display: string;
  raw: string;
  txns: Transaction[];
}

/**
 * Evaluate a merchant's charges, splitting into multiple subscriptions when
 * one merchant identity turns out to bill more than one distinct amount on
 * an interleaved, independently-recurring basis (see cluster.ts).
 */
export function evaluateGroup(g: MerchantGroup, today: Date, money: MoneyFormatter): Subscription[] {
  const whole = evaluateCluster(g.txns, g.key, g.display, g.raw, today, money);
  if (whole) return [whole];

  const clusters = clusterByAmount(g.txns).filter((c) => c.length >= 2);
  // Require the split to explain nearly the whole group, not just a lucky
  // subset — real co-billed subscriptions (a handful of stable prices) leave
  // almost no leftover; noisy variable-amount merchants (groceries, gas)
  // mostly don't cluster and would otherwise slip a coincidental subset past
  // the cadence check purely from partitioning enough small groups.
  const covered = clusters.reduce((a, c) => a + c.length, 0);
  if (clusters.length < 2 || clusters.length > 4 || covered / g.txns.length < 0.8) return [];

  const subs: Subscription[] = [];
  for (const c of clusters) {
    const amountLabel = money(median(c.map((t) => t.amount)));
    const sub = evaluateCluster(c, `${g.key}:${amountLabel}`, `${g.display} (${amountLabel})`, g.raw, today, money);
    if (sub) subs.push(sub);
  }
  return subs;
}

function evaluateCluster(
  groupTxns: Transaction[],
  key: string,
  display: string,
  raw: string,
  today: Date,
  money: MoneyFormatter,
): Subscription | null {
  const txns = [...groupTxns].sort((a, b) => a.date.getTime() - b.date.getTime());
  if (txns.length < 2) return null;

  const gaps: number[] = [];
  for (let i = 1; i < txns.length; i++) {
    gaps.push((txns[i]!.date.getTime() - txns[i - 1]!.date.getTime()) / DAY);
  }

  // Groups charging in tight clusters (e.g. a coffee shop) are not subscriptions.
  const medGap = median(gaps);
  if (medGap < 5) return null;

  const cadence = matchCadence(medGap);
  if (!cadence) return null;

  // Regularity: share of gaps fitting the cadence window (tolerates one anomaly).
  const fitting = gaps.filter((d) => d >= cadence.min && d <= cadence.max).length;
  const regularity = fitting / gaps.length;

  // Amount consistency: the typical amount should dominate; tolerates price changes.
  const amounts = txns.map((t) => t.amount);
  const medAmount = median(amounts);
  const nearMedian = amounts.filter((a) => Math.abs(a - medAmount) <= Math.max(1, medAmount * 0.25)).length;
  const amountConsistency = nearMedian / amounts.length;

  // Minimum evidence: 3 charges normally; 2 for semiannual/yearly,
  // but only when the two amounts match tightly (else it's coincidence).
  const minTxns = cadence.days >= 165 ? 2 : 3;
  if (txns.length < minTxns) return null;
  if (txns.length === 2 && Math.abs(amounts[0]! - amounts[1]!) > Math.max(1, medAmount * 0.05)) {
    return null;
  }

  const confidence = regularity * 0.6 + amountConsistency * 0.4;
  if (confidence < 0.55) return null;

  const first = txns[0]!;
  const last = txns[txns.length - 1]!;
  const currentAmount = last.amount;
  const monthlyCost = currentAmount * cadence.perMonth;

  const nextCharge = new Date(last.date.getTime() + cadence.days * DAY);
  const daysSinceLast = (today.getTime() - last.date.getTime()) / DAY;
  const status = daysSinceLast > cadence.days * 1.75 ? 'inactive' : 'active';

  const monthsRunning = (today.getTime() - first.date.getTime()) / DAY / 30.44;
  const totalSpent = amounts.reduce((a, b) => a + b, 0);

  const flags: Flag[] = [];

  // Price hike: current price meaningfully above the original.
  const firstAmount = first.amount;
  if (status === 'active' && currentAmount > firstAmount * 1.08 && currentAmount - firstAmount >= 1) {
    const pct = Math.round((currentAmount / firstAmount - 1) * 100);
    flags.push({
      type: 'price-hike',
      label: `Price up ${pct}%`,
      detail:
        `Started at ${money(firstAmount)}, now ${money(currentAmount)} — that's ` +
        `${money((currentAmount - firstAmount) * cadence.perMonth)}/mo more. Worth checking for a cheaper tier.`,
      savings: (currentAmount - firstAmount) * cadence.perMonth,
    });
  }

  // Big semiannual/annual renewal coming up.
  if (status === 'active' && cadence.days >= 165) {
    const daysToRenewal = (nextCharge.getTime() - today.getTime()) / DAY;
    if (daysToRenewal >= 0 && daysToRenewal <= 45) {
      flags.push({
        type: 'renewal-soon',
        label: `Renews in ${Math.max(1, Math.round(daysToRenewal))}d`,
        detail: `${money(currentAmount)} renewal expected ${fmtDate(nextCharge)}. Decide now whether to keep it.`,
        savings: 0,
      });
    }
  }

  // Zombie: small, long-running charge that's easy to forget.
  if (status === 'active' && monthlyCost <= 8 && monthsRunning >= 6 && cadence.days <= 35) {
    flags.push({
      type: 'zombie',
      label: 'Easy to forget',
      detail:
        `Small ${money(currentAmount)} charge running for ${Math.round(monthsRunning)} months ` +
        `(${money(totalSpent)} so far). Small leaks add up.`,
      savings: 0,
    });
  }

  if (status === 'inactive') {
    flags.push({
      type: 'stopped',
      label: 'Charges stopped',
      detail: `Last charge ${fmtDate(last.date)} — looks cancelled or lapsed. Shown for reference; not counted in totals.`,
      savings: 0,
    });
  }

  return {
    key,
    name: display,
    category: category(display, raw),
    cadence: cadence.name,
    cadenceLabel: CADENCE_LABEL[cadence.name],
    amount: currentAmount,
    monthlyCost,
    yearlyCost: monthlyCost * 12,
    confidence,
    status,
    firstDate: first.date,
    lastDate: last.date,
    nextCharge,
    chargeCount: txns.length,
    totalSpent,
    monthsRunning,
    txns,
    flags,
  };
}
